import * as React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { NotFound404 } from "@/pages/ErrorPages";
import { DEFAULT_TENANT_SLUG } from "@/lib/tenant";
import { useMultitenant } from "@/lib/modules";

/** Everything the console served before the tenant prefix existed. */
const LEGACY_PREFIXES = [
  "/overview",
  "/inventory",
  "/posture",
  "/defender",
  "/changes",
  "/history",
  "/scans",
  "/reports",
  // /admin/users, /admin/audit and /admin/platform are PLATFORM routes and
  // match directly; only the tenant half of /admin needs moving.
  "/admin/settings",
  "/admin/tenant",
] as const;

/** The default tenant's form of a legacy path. */
export function defaultTenantPath(pathname: string, search = "", hash = ""): string {
  return `/t/${DEFAULT_TENANT_SLUG}${pathname}${search}${hash}`;
}

export function isLegacyTenantPath(pathname: string): boolean {
  if (pathname === "/admin") return true;
  return LEGACY_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Where "/" goes.
 *
 * With several tenants there is no single console to open, so the deployment's
 * own screen — the tenants overview — is the answer. With one tenant that
 * screen would be a list of one, so the console opens on it directly.
 *
 * It WAITS for the answer. Guessing and correcting would flash a screen that
 * is about to be replaced, and guessing wrong sends the operator to a page
 * that answers 409.
 */
export function DefaultTenantRedirect(): React.ReactElement {
  const { enabled, isPending, isError } = useMultitenant();

  if (isPending) {
    return <LoadingSkeleton variant="page" label="Opening the console" />;
  }
  // The platform module list could not be read. The default tenant's console
  // is what every deployment has, so that is where this lands — and the
  // tenants screen refuses independently if it was in fact reachable.
  if (isError || !enabled) {
    return <Navigate to={defaultTenantPath("/overview")} replace />;
  }
  return <Navigate to="/tenants" replace />;
}

/**
 * The addresses this console used to serve.
 *
 * They resolve to the DEFAULT tenant, search string and hash preserved, so an old
 * bookmark or a link in a ticket still lands where it meant to. The redirect
 * is visible on purpose: the address bar then names the tenant, so the next
 * person to copy that link copies one that says which tenant it was about.
 *
 * Anything that is not a path this console ever served is a 404. A catch-all
 * that redirected everything would turn a typo into the default tenant's
 * overview and hide the mistake.
 */
export function LegacyRedirect(): React.ReactElement {
  const location = useLocation();
  if (!isLegacyTenantPath(location.pathname)) {
    return <NotFound404 />;
  }
  return (
    <Navigate to={defaultTenantPath(location.pathname, location.search, location.hash)} replace />
  );
}
